import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../config/api';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Search,
  Filter,
  RefreshCw,
  Eye
} from 'lucide-react';

interface Registration {
  id: string;
  registration_number: string;
  registration_type: 'customer' | 'supplier';
  status: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  entity_name: string;
  email: string;
  phone: string;
  submitted_at: string;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
}

interface PaginationInfo {
  total: number;
  limit: number | null;
  offset: number;
}

const DashboardPageModern: React.FC = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({ total: 0, limit: null, offset: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRegistrations();
  }, [statusFilter, typeFilter]);

  const fetchRegistrations = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);

      const response = await api.get(`/admin/registrations?${params.toString()}`);

      if (response.data.success) {
        setRegistrations(response.data.data.registrations);
        setPagination(response.data.data.pagination);
      } else {
        setError('Failed to fetch registrations');
      }
    } catch (err: any) {
      console.error('Error fetching registrations:', err);
      setError(err.response?.data?.error?.message || 'Failed to load registrations');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border border-yellow-200';
      case 'approved':
        return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200';
      case 'rejected':
        return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border border-red-200';
      case 'changes_requested':
        return 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeClass = (type: string) => {
    return type === 'customer'
      ? 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border border-purple-200'
      : 'bg-gradient-to-r from-indigo-100 to-indigo-50 text-indigo-800 border border-indigo-200';
  };

  const filteredRegistrations = registrations.filter((reg) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      reg.registration_number.toLowerCase().includes(query) ||
      reg.entity_name.toLowerCase().includes(query) ||
      reg.email.toLowerCase().includes(query)
    );
  });

  const handleViewDetails = (id: string) => {
    navigate(`/admin/registrations/${id}`);
  };

  const stats = [
    {
      label: 'Total Registrations',
      value: pagination.total,
      icon: Users,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Pending Review',
      value: registrations.filter((r) => r.status === 'pending').length,
      icon: Clock,
      gradient: 'from-yellow-500 to-orange-600',
      bgGradient: 'from-yellow-50 to-orange-50',
      iconColor: 'text-orange-600',
    },
    {
      label: 'Approved',
      value: registrations.filter((r) => r.status === 'approved').length,
      icon: CheckCircle,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50',
      iconColor: 'text-green-600',
    },
    {
      label: 'Rejected',
      value: registrations.filter((r) => r.status === 'rejected').length,
      icon: XCircle,
      gradient: 'from-red-500 to-rose-600',
      bgGradient: 'from-red-50 to-rose-50',
      iconColor: 'text-red-600',
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center animate-fadeIn">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-primary shadow-glow-lg mb-6">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-lg text-gray-600 font-medium">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                    <p className="text-4xl font-bold bg-gradient-to-r bg-clip-text text-transparent" style={{
                      backgroundImage: `linear-gradient(135deg, var(--tw-gradient-stops))`
                    }}>
                      <span className={`bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                        {stat.value}
                      </span>
                    </p>
                  </div>
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.bgGradient} flex items-center justify-center shadow-lg`}>
                    <stat.icon className={`w-8 h-8 ${stat.iconColor}`} strokeWidth={2.5} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
            <div className="flex items-center mb-4">
              <Filter className="w-5 h-5 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Filters & Search</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Search className="w-4 h-4 inline mr-1" />
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Name, email, or number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="changes_requested">Changes Requested</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 hover:border-gray-300"
                >
                  <option value="">All Types</option>
                  <option value="customer">Customer</option>
                  <option value="supplier">Supplier</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={fetchRegistrations}
                  className="w-full inline-flex items-center justify-center px-4 py-3 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 px-6 py-4 rounded-xl shadow-md animate-fadeIn">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-500 mr-3" />
                <span className="text-red-700 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Registrations Table */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-slideInRight">
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-accent-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="w-6 h-6 text-primary-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Registrations <span className="text-primary-600">({filteredRegistrations.length})</span>
                  </h2>
                </div>
              </div>
            </div>

            {filteredRegistrations.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gray-100 mb-4">
                  <FileText className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No registrations found</h3>
                <p className="text-sm text-gray-500">
                  {searchQuery || statusFilter || typeFilter
                    ? 'Try adjusting your filters to see more results'
                    : 'No registrations have been submitted yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Registration #
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRegistrations.map((registration, index) => (
                      <tr
                        key={registration.id}
                        className="hover:bg-primary-50 transition-colors duration-150"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 font-mono">
                            {registration.registration_number}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadgeClass(registration.registration_type)}`}>
                            {registration.registration_type.charAt(0).toUpperCase() + registration.registration_type.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{registration.entity_name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {registration.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(registration.status)}`}>
                            {registration.status.replace('_', ' ').charAt(0).toUpperCase() + registration.status.replace('_', ' ').slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(registration.submitted_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(registration.id)}
                            className="inline-flex items-center text-primary-600 hover:text-primary-900 font-semibold group"
                          >
                            <Eye className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
    </AdminLayout>
  );
};

export default DashboardPageModern;
